// src/utils/measureTextWidth.js
//
// Estimates the rendered pixel width of a text string.
// Uses a character-count heuristic calibrated for Lato 14px (~8.5px per character).
// Canvas-based measurement is intentionally avoided because web fonts (Lato) are not
// guaranteed to be loaded in the canvas context at call time, causing under-measurement.
//
// CJK, Hangul, full-width, and common emoji characters are treated as wider (~14px)
// to avoid under-estimating width and suppressing tooltips for non-Latin folder names.
//
// Mirrors packages/nova/src/js/shared/utils/measureTextWidth.ts in the ui workspace so
// truncation-conditional tooltip behavior is identical across both apps.

const LATIN_CHAR_WIDTH = 8.5;
const WIDE_CHAR_WIDTH = 14;

function isWideChar(codePoint) {
  return (
    (codePoint >= 0x4e00 && codePoint <= 0x9fff) || // CJK Unified Ideographs
    (codePoint >= 0x3400 && codePoint <= 0x4dbf) || // CJK Extension A
    (codePoint >= 0x20000 && codePoint <= 0x2a6df) || // CJK Extension B
    (codePoint >= 0xac00 && codePoint <= 0xd7af) || // Hangul syllables
    (codePoint >= 0xff01 && codePoint <= 0xff60) || // Fullwidth forms
    (codePoint >= 0x1f300 && codePoint <= 0x1f9ff) // Common emoji
  );
}

/**
 * @param {string} text - The string to measure
 * @returns {number} estimated pixel width
 */
export function measureTextWidth(text) {
  let width = 0;
  for (const char of text) {
    const codePoint = char.codePointAt(0);
    width += codePoint !== undefined && isWideChar(codePoint) ? WIDE_CHAR_WIDTH : LATIN_CHAR_WIDTH;
  }
  return width;
}
